import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'yet-another-react-lightbox/styles.css';
import './App.css';
import AskContainer from './Components/AskContainer';
import BackButton from './Components/BackButton';
import ButtonsContainer from './Components/ButtonsContainer';
import ChatAvatars from './Components/ChatAvatars';
import ChatInputContainer from './Components/ChatInputContainer';
import ChatMessages from './Components/ChatMessages';
import CloseButton from './Components/CloseButton';
import Loader from './Components/Loader';
import NoMessageBox from './Components/NoMessageBox';
import WelcomeBox from './Components/WelcomeBox';
import { useChatApi } from './Services/ChatApiProvider';
import { IS_EXTERNAL_LAUNCHER, MSG, PROTOCOL_VERSION } from './Services/parentBridge';
import './StaticBox.css';

function App() {
  const {
    loading,
    isActive,
    channelSettings,
    messages,
    activate,
    postToParent,
    subscribeToHostCommands,
  } = useChatApi();

  const headerLogoText =
    (channelSettings?.headerLogoText ?? '').toString().trim() ||
    'SUR<i>E</i> Support <span>Agent</span>';
  const footerLogoText =
    (channelSettings?.footerLogoText ?? '').toString().trim() ||
    'Powered by <span class="footer-logo">SUR<i>E</i></span> Agent';

  // Log channelSettings whenever it updates
  // useEffect(() => {
  //   if (channelSettings) {
  //     console.log('channelSettings', channelSettings);
  //   } else {
  //     console.log('channelSettings', null);
  //   }
  // }, [channelSettings]);

  // ✅ Memoize derived values (prevents recalculation and effect churn)
  const isMobileDevice = useMemo(
    () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    [],
  );

  const chatType = useMemo(() => {
    const v = (channelSettings?.chatType ?? 'widget').toString().trim().toLowerCase();
    return v === 'static-box' ? 'static-box' : 'widget';
  }, [channelSettings?.chatType]);

  const showFirst = useMemo(() => {
    return typeof channelSettings?.isFirstScreenEnabled === 'boolean'
      ? channelSettings.isFirstScreenEnabled
      : true;
  }, [channelSettings?.isFirstScreenEnabled]);

  // Local UI states
  const [isChatActive, setIsChatActive] = useState(!showFirst);
  const [isInputActive, setIsInputActive] = useState(false);
  const [email, setEmail] = useState('');
  const [request, setRequest] = useState('');
  const chatScrollRef = useRef(null);

  // With an external launcher the host owns the open/close button, so the widget
  // starts closed and stays that way until the host asks for it.
  const [isFolded, setIsFolded] = useState(IS_EXTERNAL_LAUNCHER);
  const [showChatContainer, setShowChatContainer] = useState(false);

  const isFoldedRef = useRef(IS_EXTERNAL_LAUNCHER);
  const foldSourceRef = useRef('init');
  const openRef = useRef(false);

  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Single entry point for every fold change — header close button, launcher bubble,
  // channel settings and host commands all route through here, so the local state and
  // the state the host is told about can never drift apart.
  const applyFold = useCallback((next, source) => {
    const value = typeof next === 'function' ? next(isFoldedRef.current) : Boolean(next);

    isFoldedRef.current = value;
    foldSourceRef.current = source;
    setIsFolded((prev) => (prev === value ? prev : value));
    setShowChatContainer((prev) => (prev === !value ? prev : !value));
  }, []);

  // ✅ Apply folded state ONLY when the value actually changes
  useEffect(() => {
    // externalLauncher deliberately overrides the channel's isFolded setting — but a
    // static box has nothing to fold, and forcing it would leave an empty container.
    if (IS_EXTERNAL_LAUNCHER && chatType !== 'static-box') return;
    if (!isActive) return;
    if (typeof channelSettings?.isFolded !== 'boolean') return;

    applyFold(channelSettings.isFolded, 'settings');
  }, [isActive, channelSettings?.isFolded, applyFold, chatType]);

  // The panel is open only once it is both unfolded and actually rendered — before
  // the channel settings land, neither the panel nor the launcher is on screen.
  const isOpen = showChatContainer && !isFolded;

  useEffect(() => {
    openRef.current = isOpen;
    postToParent({
      type: MSG.STATE,
      v: PROTOCOL_VERSION,
      payload: { isOpen, source: foldSourceRef.current },
    });
  }, [isOpen, postToParent]);

  useEffect(() => {
    return subscribeToHostCommands((action) => {
      // A static box has no open/close axis; folding it would leave an empty gap.
      if (chatType === 'static-box') {
        postToParent({
          type: MSG.STATE,
          v: PROTOCOL_VERSION,
          payload: { isOpen: true, source: 'unsupported' },
        });
        return;
      }

      if (action === 'open') applyFold(false, 'external');
      else if (action === 'close') applyFold(true, 'external');
      else if (action === 'toggle') applyFold((prev) => !prev, 'external');
      else if (action === 'sync') {
        postToParent({
          type: MSG.STATE,
          v: PROTOCOL_VERSION,
          payload: { isOpen: openRef.current, source: 'sync' },
        });
      }
    });
  }, [subscribeToHostCommands, postToParent, applyFold, chatType]);

  // Keep isChatActive in sync with showFirst, but avoid re-setting same value
  useEffect(() => {
    const desired = !showFirst;
    setIsChatActive((prev) => (prev !== desired ? desired : prev));
  }, [showFirst]);

  // Auto-activate once when first screen is disabled and channel is active
  const autoActivatedRef = useRef(false);
  useEffect(() => {
    if (!showFirst && isActive && !autoActivatedRef.current) {
      // Nothing is on screen yet behind an external launcher, and activate() asks
      // Safari for storage access — keep it for the moment the widget opens.
      if (IS_EXTERNAL_LAUNCHER && isFolded) return;
      autoActivatedRef.current = true;
      setIsChatActive(true);
      activate(); // safe; provider handles the platform specifics
    }
  }, [showFirst, isActive, activate, isFolded]);

  // Chat color variables
  useEffect(() => {
    const primaryColor = channelSettings?.chatPrimaryColor?.trim();
    const secondaryColor = channelSettings?.chatSecondaryColor?.trim();

    if (primaryColor)
      document.documentElement.style.setProperty('--chat-primary-color', primaryColor);
    else document.documentElement.style.removeProperty('--chat-primary-color');

    if (secondaryColor)
      document.documentElement.style.setProperty('--chat-secondary-color', secondaryColor);
    else document.documentElement.style.removeProperty('--chat-secondary-color');
  }, [channelSettings?.chatPrimaryColor, channelSettings?.chatSecondaryColor]);

  // Single resize/fullscreen notifier for both modes
  useEffect(() => {
    const calcPayload = () => {
      if (chatType === 'static-box') {
        const fullscreen = isMobileDevice && isChatActive;
        return {
          mode: 'static-box',
          fullscreen,
          folded: false,
          width: window.innerWidth,
          height: fullscreen ? window.innerHeight : 788,
        };
      } else {
        const fullscreen = isMobileDevice && !isFolded;
        // The built-in 70px bubble never renders behind an external launcher, so the
        // iframe keeps panel geometry and the host only toggles its visibility.
        const collapsed = isFolded && !IS_EXTERNAL_LAUNCHER;
        return {
          mode: 'widget',
          fullscreen,
          folded: isFolded,
          width: collapsed ? 70 : 380,
          height: collapsed ? 70 : fullscreen ? window.innerHeight : 788,
        };
      }
    };

    const post = () => {
      // Layout only, and it has to land before the handshake so the host paints the
      // right shape on the first frame — hence '*' instead of the handshaken origin.
      window.parent?.postMessage({ type: MSG.RESIZE, payload: calcPayload() }, '*');
    };

    post();
    const onResize = () => post();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [chatType, isFolded, isChatActive, isMobileDevice]);

  // MOBILE keyboard handler (unchanged logic; no state churn)
  useEffect(() => {
    if (!isMobileDevice) return;

    const el = chatContainerRef.current;
    if (!el) return;

    const vv = window.visualViewport || null;
    let rafId = 0;

    const state = { enabled: false, savedScrollY: 0 };

    const getKbOffset = () => {
      const layoutH = document.documentElement?.clientHeight || window.innerHeight;
      const a = vv ? Math.max(0, layoutH - Math.round(vv.height)) : 0;
      const b = vv
        ? Math.max(0, window.innerHeight - Math.round(vv.height + (vv.offsetTop || 0)))
        : 0;
      const c = vv ? Math.max(0, window.innerHeight - Math.round(vv.height)) : 0;
      return Math.max(a, b, c, 0);
    };

    const applyStick = () => {
      if (!state.enabled) return;
      const kb = getKbOffset();
      el.style.transform = kb > 0 ? `translate3d(0,-${kb}px,0)` : '';
    };

    const scheduleApply = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(applyStick);
    };

    const onViewportChange = () => state.enabled && scheduleApply();
    const onScroll = () => {
      if (!state.enabled) return;
      if (window.scrollY !== state.savedScrollY) window.scrollTo(0, state.savedScrollY);
      scheduleApply();
    };

    const onFocusIn = (e) => {
      if (e.target?.closest?.('.input-area')) {
        state.enabled = true;
        state.savedScrollY = window.scrollY;
        el.style.willChange = 'transform';
        scheduleApply();
        setTimeout(scheduleApply, 200);
        setTimeout(scheduleApply, 400);
      }
    };

    const onFocusOut = () => {
      const stillFocused = document.querySelector('.input-area :focus');
      if (!stillFocused) {
        state.enabled = false;
        el.style.transform = '';
        el.style.willChange = '';
      }
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onViewportChange, { passive: true });
    window.addEventListener('orientationchange', onViewportChange, { passive: true });
    vv?.addEventListener('resize', onViewportChange);
    vv?.addEventListener('scroll', onViewportChange);

    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('orientationchange', onViewportChange);
      vv?.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('scroll', onViewportChange);
      cancelAnimationFrame(rafId);
    };
  }, [isMobileDevice]);

  // Clear transform when widget is folded
  useEffect(() => {
    if (chatContainerRef.current && isFolded) {
      chatContainerRef.current.style.transform = '';
      chatContainerRef.current.style.willChange = '';
    }
  }, [isFolded]);

  const activateChat = () => {
    if (!isChatActive) setIsChatActive(true);
    activate();
  };

  const deactivateChat = () => setIsChatActive(false);
  const handleInputBlur = () => {
    if (!email && !request) setIsInputActive(false);
  };
  const handleRequestChange = (e) => setRequest(e.target.value);
  const handleToggleFold = () => applyFold((prev) => !prev, 'internal');

  if (loading) return <Loader />;

  return (
    <>
      <motion.div
        ref={chatContainerRef}
        className={`chat-container ${isChatActive ? 'chat-screen' : ''} ${isMobileDevice ? 'mobile' : ''} ${chatType === 'static-box' ? 'static-box' : ''} ${channelSettings?.showFooterLogo === false ? 'no-footer-logo' : ''}`}
        style={{
          overflow: 'hidden',
          display: showChatContainer ? '' : 'none',
          borderRadius: isFolded ? '100px' : '',
        }}
        animate={
          !IS_EXTERNAL_LAUNCHER && channelSettings?.isFolded ? { opacity: isFolded ? 0 : 1 } : {}
        }
        transition={{ duration: 0.2 }}
      >
        {!isFolded && (
          <>
            <div className="chat-header" ref={headerRef}>
              <BackButton onClick={deactivateChat} isChatActive={isChatActive} />
              {isChatActive && chatType === 'widget' && (
                <div
                  className="sure-subtitle widget"
                  dangerouslySetInnerHTML={{ __html: headerLogoText }}
                />
              )}
              {!isFolded && chatType === 'widget' && <CloseButton onClick={handleToggleFold} />}
              <ChatAvatars isChatActive={isChatActive} chatType={chatType} />
              <WelcomeBox isChatActive={isChatActive} chatType={chatType} />
            </div>

            <div className="chat-body">
              <AskContainer onClick={activateChat} isChatActive={isChatActive} />
              <div className="messages-container" ref={chatScrollRef}>
                {channelSettings?.noMessageBox && (
                  <NoMessageBox onAskClick={activateChat} isChatActive={isChatActive} />
                )}
                <ChatMessages isChatActive={isChatActive} scrollContainerRef={chatScrollRef} />
              </div>
            </div>

            <div className="chat-footer" ref={footerRef}>
              {!isChatActive ? (
                <ButtonsContainer onChatClick={activateChat} />
              ) : (
                <ChatInputContainer
                  isChatActive={isChatActive}
                  request={request}
                  onBlur={handleInputBlur}
                  onRequestChange={handleRequestChange}
                />
              )}
              {channelSettings?.showFooterLogo !== false && (
                <div className="powered-by">
                  <span dangerouslySetInnerHTML={{ __html: footerLogoText }} />
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>

      {!IS_EXTERNAL_LAUNCHER && isFolded && (
        <div
          className={`chat-unfold-icon ${!isFolded ? 'active' : ''}`}
          onClick={handleToggleFold}
        />
      )}
    </>
  );
}

export default App;
